import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { BackofficeAuthUser } from '@/features/backoffice2020/types/backoffice-auth.types';
import { normalizeBackofficeAuthUser } from '@/features/backoffice2020/utils/normalize-backoffice-auth-user';
import { STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { storage } from '@/shared/utils/storage';

export interface BackofficeAuthState {
  accessToken: string | null;
  user: BackofficeAuthUser | null;
  isAuthenticated: boolean;
}

function loadPersistedBackofficeAuth(): BackofficeAuthState {
  const accessToken = storage.getString(STORAGE_KEYS.BACKOFFICE_ACCESS_TOKEN);
  const user = normalizeBackofficeAuthUser(
    storage.get(STORAGE_KEYS.BACKOFFICE_USER),
  );

  return {
    accessToken,
    user,
    isAuthenticated: Boolean(accessToken),
  };
}

const initialState: BackofficeAuthState = loadPersistedBackofficeAuth();

interface SetBackofficeSessionPayload {
  accessToken: string;
  user?: BackofficeAuthUser | null;
  rememberMe?: boolean;
}

const backofficeAuthSlice = createSlice({
  name: 'backofficeAuth',
  initialState,
  reducers: {
    setBackofficeSession: (
      state,
      action: PayloadAction<SetBackofficeSessionPayload>,
    ) => {
      const { accessToken, user = null, rememberMe = true } = action.payload;
      state.accessToken = accessToken;
      state.user = user;
      state.isAuthenticated = true;

      if (rememberMe) {
        storage.setString(STORAGE_KEYS.BACKOFFICE_ACCESS_TOKEN, accessToken);
        storage.setString(STORAGE_KEYS.BACKOFFICE_REMEMBER_ME, 'true');
        if (user) {
          storage.set(STORAGE_KEYS.BACKOFFICE_USER, user);
        } else {
          storage.remove(STORAGE_KEYS.BACKOFFICE_USER);
        }
      } else {
        storage.remove(STORAGE_KEYS.BACKOFFICE_ACCESS_TOKEN);
        storage.remove(STORAGE_KEYS.BACKOFFICE_REMEMBER_ME);
        storage.remove(STORAGE_KEYS.BACKOFFICE_USER);
      }
    },

    clearBackofficeSession: (state) => {
      state.accessToken = null;
      state.user = null;
      state.isAuthenticated = false;
      storage.clear([
        STORAGE_KEYS.BACKOFFICE_ACCESS_TOKEN,
        STORAGE_KEYS.BACKOFFICE_REMEMBER_ME,
        STORAGE_KEYS.BACKOFFICE_USER,
      ]);
    },
  },
});

export const { setBackofficeSession, clearBackofficeSession } =
  backofficeAuthSlice.actions;

export const selectBackofficeAccessToken = (state: {
  backofficeAuth: BackofficeAuthState;
}) => state.backofficeAuth.accessToken;

export const selectBackofficeUser = (state: {
  backofficeAuth: BackofficeAuthState;
}) => state.backofficeAuth.user;

export const selectIsBackofficeAuthenticated = (state: {
  backofficeAuth: BackofficeAuthState;
}) => state.backofficeAuth.isAuthenticated;

export default backofficeAuthSlice.reducer;
